import React, { useState, useEffect, useCallback } from 'react';
import CapMap from './CapMap';
import NearbyComuniPanel from './NearbyComuniPanel';
import api from '../api/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, CheckCircle, WarningCircle, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const CoverageEditor = ({ onSaved, compact = false }) => {
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [form, setForm] = useState({ region: '', province: '', comune: '', street: '' });
  const [picked, setPicked] = useState(''); // comune scelto dall'elenco (non il testo digitato)
  const [comuneOptions, setComuneOptions] = useState([]);
  const [capData, setCapData] = useState(null);
  const [zones, setZones] = useState([]);
  const [selected, setSelected] = useState([]);
  const [address, setAddress] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [staff, setStaff] = useState(null);
  const [areas, setAreas] = useState([]);   // altri comuni coperti (oltre a quello in modifica)
  const [nearby, setNearby] = useState([]); // Centri con comune a CAP unico: comuni vicini

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get('/api/geo/italian-regions'),
          api.get('/api/coverage/me'),
        ]);
        setRegions(r.data || []);
        const cov = c.data || {};
        const list = (cov.areas || []).length
          ? cov.areas.map((a) => ({ region: a.regione, province: a.provincia, comune: a.comune, caps: a.caps || [], direct: a.direct }))
          : (cov.comune ? [{ region: cov.region, province: cov.province, comune: cov.comune, caps: cov.caps || [] }] : []);
        if (list.length) {
          setForm({ region: list[0].region || '', province: list[0].province || '', comune: list[0].comune, street: cov.street || '' });
          setPicked(list[0].comune);
          setSelected(list[0].caps || []);
          setAreas(list.slice(1));
        }
        setInfo(cov);
        setNearby((cov.nearby_areas || []).map((a) => ({ region: a.regione, province: a.provincia, comune: a.comune, caps: a.caps || [] })));
        if (cov.is_center) {
          api.get('/api/coverage/staff-coverage')
            .then((r) => setStaff(r.data)).catch(() => setStaff(null));
        }
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!form.region) { setProvinces([]); return; }
    api.get(`/api/geo/provinces?region=${encodeURIComponent(form.region)}`)
      .then((r) => setProvinces(r.data || [])).catch(() => setProvinces([]));
  }, [form.region]);

  const loadCaps = useCallback(async (comune) => {
    if (!comune) { setCapData(null); setZones([]); return; }
    try {
      const quick = await api.get(`/api/geo/caps?comune=${encodeURIComponent(comune)}`);
      setCapData(quick.data);
      // Confini CAP (poligoni): se disponibili sostituiscono i punti sulla mappa
      api.get(`/api/geo/cap-zones?comune=${encodeURIComponent(comune)}`)
        .then((r) => setZones(r.data.features || [])).catch(() => setZones([]));
      const merge = (data) => setCapData((prev) => (prev && prev.comune === data.comune
        ? { ...data, caps: data.caps.map((c, i) => (c.latitude ? c : (prev.caps[i] || c))) }
        : data));
      const first = await api.get(`/api/geo/caps?comune=${encodeURIComponent(comune)}&with_points=true&max_points=10`);
      merge(first.data);
      if ((first.data.caps || []).length > 10) {
        api.get(`/api/geo/caps?comune=${encodeURIComponent(comune)}&with_points=true&max_points=60`)
          .then((r) => merge(r.data)).catch(() => {});
      }
    } catch { setCapData(null); }
  }, []);

  useEffect(() => { loadCaps(picked); }, [picked, loadCaps]);

  const searchComuni = async (q) => {
    setForm((f) => ({ ...f, comune: q }));
    setPicked('');
    setAddress(null);
    if (q.length < 2 || !form.province) { setComuneOptions([]); return; }
    try {
      const r = await api.get(`/api/geo/comuni?province=${encodeURIComponent(form.province)}&q=${encodeURIComponent(q)}&limit=8`);
      setComuneOptions(r.data.items || []);
    } catch { setComuneOptions([]); }
  };

  const pickComune = (c) => {
    setForm((f) => ({ ...f, comune: c.comune }));
    setPicked(c.comune);
    setComuneOptions([]);
    setSelected([]);
    setNearby([]);
    setAddress(null);
  };

  const checkAddress = async () => {
    if (!form.street || !form.comune) { toast.error('Inserisci comune e via'); return; }
    setChecking(true);
    try {
      const r = await api.post('/api/geo/validate-address', {
        street: form.street, comune: form.comune, province: form.province, region: form.region,
      });
      setAddress(r.data);
      if (r.data.valid) {
        toast.success(`Indirizzo confermato${r.data.cap ? ` · CAP ${r.data.cap}` : ''}`);
        if (r.data.cap && !selected.includes(r.data.cap)) setSelected((s) => [...s, r.data.cap]);
      } else {
        toast.warning('Indirizzo non confermato: controlla la via');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore validazione indirizzo');
    } finally { setChecking(false); }
  };

  const toggleCap = (cap) => setSelected((s) => (s.includes(cap) ? s.filter((x) => x !== cap) : [...s, cap]));
  const allCaps = (capData?.caps || []).map((c) => c.cap);
  const selectAll = () => setSelected(allCaps);
  const clearAll = () => setSelected([]);

  const currentArea = () => ({
    region: form.region, province: form.province, comune: form.comune, caps: selected,
  });

  const addArea = () => {
    if (!form.comune || selected.length === 0) {
      toast.error('Completa il comune corrente prima di aggiungerne un altro');
      return;
    }
    setAreas((a) => [...a, currentArea()]);
    setForm({ region: form.region, province: form.province, comune: '', street: form.street });
    setPicked('');
    setSelected([]);
    setCapData(null);
    setZones([]);
    toast.success(`${form.comune} aggiunto: ora scegli il nuovo comune`);
  };

  const editArea = (i) => {
    const target = areas[i];
    const rest = areas.filter((_, idx) => idx !== i);
    setAreas(form.comune && selected.length ? [...rest, currentArea()] : rest);
    setForm((f) => ({ ...f, region: target.region, province: target.province, comune: target.comune }));
    setPicked(target.comune);
    setSelected(target.caps || []);
  };

  const removeArea = (i) => setAreas((a) => a.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.region || !form.province || !form.comune) { toast.error('Completa regione, provincia e comune'); return; }
    if (selected.length === 0) { toast.error('Seleziona almeno un CAP sulla mappa'); return; }
    setSaving(true);
    try {
      const r = await api.put('/api/coverage/me', {
        region: form.region, province: form.province, comune: form.comune,
        caps: selected, street: form.street || null,
        extra_areas: areas.map((a) => ({ region: a.region, province: a.province, comune: a.comune, caps: a.caps })),
        ...(info?.is_center ? { nearby_areas: allCaps.length === 1 ? nearby : [] } : {}),
      });
      const direct = (r.data.areas || []).filter((a) => a.direct);
      if (direct.length) {
        toast.info(`${direct.map((a) => a.comune).join(', ')}: nessun Centro in zona, le richieste arrivano direttamente a te`);
      }
      const linked = r.data.linked_centers || [];
      if (linked.length > 0) {
        toast.success(`Copertura salvata. Sei stato assegnato a: ${linked.map((c) => c.name).join(', ')}`);
      } else if (r.data.radius_km) {
        toast.success(`Copertura salvata, limitata a ${r.data.radius_km} km (altri Centri in città)`);
      } else {
        toast.success('Copertura salvata');
      }
      const c = await api.get('/api/coverage/me');
      setInfo(c.data);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio copertura');
    } finally { setSaving(false); }
  };

  const center = React.useMemo(() => (capData?.center?.latitude
    ? [capData.center.latitude, capData.center.longitude]
    : [41.9028, 12.4964]), [capData?.center?.latitude, capData?.center?.longitude]);
  const points = React.useMemo(() => (capData?.caps || []).filter((c) => c.latitude), [capData]);

  return (
    <div className="space-y-5" data-testid="coverage-editor">
      <Card className="rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={20} weight="duotone" className="text-primary" />
          <div>
            <p className="font-heading font-medium">Zona di copertura</p>
            <p className="text-xs text-foreground/60">
              {info?.is_center
                ? 'Scegli il comune e i CAP in cui opera il Centro: le richieste di quei CAP arriveranno a te.'
                : 'Puoi coprire più comuni: nei comuni senza Centri le richieste arrivano direttamente a te.'}
            </p>
          </div>
        </div>

        {!info?.is_center && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="coverage-areas">
            {areas.map((a, i) => (
              <span key={`${a.comune}-${i}`} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs">
                <button type="button" onClick={() => editArea(i)} className="hover:text-primary" data-testid={`area-edit-${a.comune}`}>
                  {a.comune} · {a.caps.length} CAP
                </button>
                <button type="button" onClick={() => removeArea(i)} className="w-5 h-5 rounded-full hover:bg-slate-200 text-foreground/50" data-testid={`area-remove-${a.comune}`}>
                  ×
                </button>
              </span>
            ))}
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={addArea} data-testid="coverage-add-area">
              + Aggiungi un altro comune
            </Button>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-xs">Regione *</Label>
            <Select value={form.region} onValueChange={(v) => { setForm({ ...form, region: v, province: '', comune: '' }); setPicked(''); setAddress(null); }}>
              <SelectTrigger data-testid="coverage-region"><SelectValue placeholder="Scegli" /></SelectTrigger>
              <SelectContent>
                {regions.map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Provincia *</Label>
            <Select value={form.province} onValueChange={(v) => { setForm({ ...form, province: v, comune: '' }); setPicked(''); setAddress(null); }} disabled={!form.region}>
              <SelectTrigger data-testid="coverage-province"><SelectValue placeholder="Scegli" /></SelectTrigger>
              <SelectContent>
                {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <Label className="text-xs">Comune *</Label>
            <Input
              value={form.comune} onChange={(e) => searchComuni(e.target.value)}
              placeholder="Scrivi il comune" disabled={!form.province} data-testid="coverage-comune"
            />
            {comuneOptions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                {comuneOptions.map((c) => (
                  <button
                    key={c.comune} onClick={() => pickComune(c)} data-testid={`comune-option-${c.comune}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                  >
                    {c.comune} <span className="text-foreground/50">({c.caps.length} CAP)</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Via della sede / punto di partenza <span className="text-foreground/50">(consigliata)</span></Label>
            <Input
              value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })}
              placeholder="Es. Corso Buenos Aires 10" data-testid="coverage-street"
            />
          </div>
          <Button variant="outline" className="rounded-full h-10" onClick={checkAddress} disabled={checking} data-testid="coverage-check-address">
            {checking ? 'Verifica…' : 'Verifica indirizzo'}
          </Button>
        </div>
        {address && (
          <p className={`text-xs mt-2 flex items-center gap-1 ${address.valid ? 'text-emerald-700' : 'text-amber-800'}`} data-testid="coverage-address-result">
            {address.valid ? <CheckCircle size={13} /> : <WarningCircle size={13} />}
            {address.valid ? `Confermato: ${address.formatted}` : 'Indirizzo non confermato nel comune indicato'}
          </p>
        )}
      </Card>

      {((picked && capData && !(capData.caps || []).length) || (!picked && form.comune.length >= 2 && comuneOptions.length === 0)) && (
        <Card className="rounded-2xl p-4 text-xs bg-amber-50 border-amber-200" data-testid="coverage-comune-unknown">
          Comune «{form.comune}» non riconosciuto: scrivilo di nuovo e scegli una voce dall'elenco dei suggerimenti.
        </Card>
      )}

      {capData && (
        <Card className="rounded-2xl p-5" data-testid="coverage-map-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-sm font-medium">
              <div>CAP di {capData.comune} · <span className="text-primary">{selected.length}</span>/{allCaps.length} selezionati</div>
              <div className="text-xs font-normal text-foreground/55 mt-0.5">
                {zones.length > 0
                  ? 'Clicca sui confini delle zone CAP sulla mappa oppure usa l\'elenco qui sotto'
                  : 'Clicca sui punti CAP sulla mappa oppure usa l\'elenco qui sotto'}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full h-8" onClick={selectAll} data-testid="coverage-select-all">
                Seleziona tutta la città
              </Button>
              <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={clearAll} data-testid="coverage-clear">
                Azzera
              </Button>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden border border-border mb-3" style={{ height: compact ? 320 : 480 }}>
            <CapMap
              center={center}
              points={points}
              zones={zones}
              selected={selected}
              onToggle={toggleCap}
              height={compact ? 320 : 480}
              radiusCenter={info?.radius_km && address?.latitude ? [address.latitude, address.longitude] : null}
              radiusKm={info?.radius_km || 0}
              staffCounts={staff ? staff.counts : null}
            />
          </div>

          {staff && (
            <div className="flex items-center gap-4 flex-wrap text-xs text-foreground/70 mb-3" data-testid="coverage-legend">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3.5 h-3.5 rounded-sm" style={{ background: '#1565C0' }} />
                Coperta dall'organico
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3.5 h-3.5 rounded-sm border-2 border-dashed" style={{ background: '#F59E0B', borderColor: '#B45309' }} />
                Scoperta: nessuno in organico
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3.5 h-3.5 rounded-sm" style={{ background: '#CFD8DC' }} />
                Fuori copertura
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5" data-testid="coverage-cap-chips">
            {allCaps.map((cap) => (
              <button
                key={cap} onClick={() => toggleCap(cap)} data-testid={`cap-chip-${cap}`}
                className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                  selected.includes(cap)
                    ? (staff && !(staff.counts?.[cap] > 0)
                      ? 'bg-amber-100 text-amber-900 border-amber-400 border-dashed'
                      : 'bg-primary text-white border-primary')
                    : 'bg-white text-foreground/70 border-border hover:border-primary/50'}`}
                title={staff ? `${staff.counts?.[cap] || 0} professionisti in organico` : undefined}
              >
                {cap}{staff && selected.includes(cap) ? ` · ${staff.counts?.[cap] || 0}` : ''}
              </button>
            ))}
          </div>

          {staff && (
            <div
              className={`mt-4 p-3 rounded-lg text-xs border ${staff.uncovered.length
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
              data-testid="coverage-staff-gaps"
            >
              {staff.uncovered.length
                ? <>Organico: <strong>{staff.staff_count}</strong> professionisti · <strong>{staff.uncovered.length}</strong> CAP
                  della tua copertura senza nessuno in organico ({staff.uncovered.slice(0, 8).join(', ')}
                  {staff.uncovered.length > 8 ? '…' : ''}). Aggiungi professionisti o estendi la loro zona dall'area Organico.</>
                : <>Tutti i CAP della tua copertura hanno almeno un professionista in organico ({staff.staff_count} in totale).</>}
            </div>
          )}

          {info?.is_center && allCaps.length === 1 && (
            <NearbyComuniPanel comune={picked} address={address} value={nearby} onChange={setNearby} compact={compact} />
          )}

          {(info?.centers || []).length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs" data-testid="coverage-centers">
              Sei assegnato a: <strong>{info.centers.map((c) => c.name).join(', ')}</strong>. Le richieste
              della zona arrivano al Centro che te le assegna.
              {(info.centers || []).some((c) => (c.caps || []).length > 0) && (
                <ul className="mt-2 space-y-0.5">
                  {info.centers.filter((c) => (c.caps || []).length).map((c) => (
                    <li key={c.professional_id}>
                      <strong>{c.name}</strong>: CAP {c.caps.join(', ')}
                      {c.distance_km ? ` · Centro più vicino (${c.distance_km} km)` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {info?.radius_km && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs" data-testid="coverage-radius-note">
              In questa città operano più Centri: la tua copertura effettiva è limitata a un raggio di{' '}
              <strong>{info.radius_km} km</strong> dal tuo indirizzo.
            </div>
          )}

          <div className="mt-4">
            <Button className="rounded-full" onClick={save} disabled={saving} data-testid="coverage-save">
              <FloppyDisk size={15} className="mr-1" /> {saving ? 'Salvataggio…' : 'Salva copertura'}
            </Button>
          </div>
        </Card>
      )}

      {info?.caps_effective?.length > 0 && (
        <div className="text-xs text-foreground/60 flex items-center gap-1" data-testid="coverage-summary">
          Copertura attiva: {info.caps_effective.length} CAP in {info.comune}
          {info.full_city ? ' (tutta la città)' : ''}
          {(info.nearby_areas || []).length > 0 && ` + ${info.nearby_areas.length} comuni vicini (${info.nearby_areas.map((a) => a.comune).join(', ')})`}
          {(info.caps || []).length > 0 && <Badge variant="outline" className="ml-1 text-[10px]">aggiornata</Badge>}
        </div>
      )}
    </div>
  );
};

export default CoverageEditor;
